import {
  addSignTemplateDetail,
  goodsManagementGetQuotaContractInfoPage,
  updateSignTemplateDetail,
} from '@/services/goods'
import { httpOrgList } from '@/services/homeService'
import { Button, message } from 'antd'
import { useEffect, useRef, useState, useCallback } from 'react'
import { SDrawerOrModal, SGeneralForms } from 'shj-components'
import dayjs from 'dayjs'

const AddOrEditDrawer = (props: {
  id: string // 模版ID
  budgetList: any // 报价类型列表
  isAdd: boolean // 是否新增
  listRef: any // 列表实例
  clickInfo: any // 点击的行信息
  open: boolean
  onCancel: () => void
}) => {
  const { id, isAdd, open, onCancel, budgetList, listRef, clickInfo } = props
  const formRef = useRef<any>()
  const [companyList, setCompanyList] = useState<any>([])
  const [quoteContractList, setQuoteContractList] = useState<any>([])
  const [budgetTypeValue, setBudgetTypeValue] = useState<string>('')
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [quoteContractLoading, setQuoteContractLoading] = useState<boolean>(false)

  // 获取选项内容 (报价类型 适用门店)
  const getQuoteType = async () => {
    // 适用门店
    const companyRes: any = await httpOrgList()
    setCompanyList([{ name: '通用', id: '0' }, ...companyRes])
  }

  // 获取报价合同配置
  const getQuoteContract = useCallback(async () => {
    setQuoteContractLoading(true)
    const res: any = await goodsManagementGetQuotaContractInfoPage({
      params: { pageId: 1, pageSize: 500 },
      data: { enabled: true, quoteType: budgetTypeValue },
    })
    const list = res.list.map((item: any) => ({
      label: item.title,
      value: item.id,
    }))
    setQuoteContractList(list)
    setQuoteContractLoading(false)
  }, [budgetTypeValue])

  // 新增模版配置
  const addTemplateDetail = () => {
    const values = formRef.current?.onFormRef()?.getFieldsValue()
    const request = {
      ...values,
      startDate: values.startDate.format('YYYY-MM-DD'),
      endDate: values?.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
      signTemplateId: id,
    }
    setSubmitLoading(true)
    addSignTemplateDetail(request)
      .then(() => {
        message.success('新增成功')
        onCancel()
        listRef.current.reload()
      })
      .finally(() => {
        setSubmitLoading(false)
      })
  }

  // 修改模版配置
  const editTemplateDetail = () => {
    const values = formRef.current?.onFormRef()?.getFieldsValue()
    const request = {
      ...values,
      id: clickInfo.id, // 使用点击的模版配置ID
      startDate: values.startDate.format('YYYY-MM-DD'),
      endDate: values?.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
    }
    setSubmitLoading(true)
    updateSignTemplateDetail(request)
      .then(() => {
        message.success('修改成功')
        onCancel()
        listRef.current.reload()
      })
      .finally(() => {
        setSubmitLoading(false)
      })
  }

  const onSave = () => {
    // 校验必填项
    if (isAdd) {
      formRef.current
        ?.onFormRef()
        ?.validateFields()
        .then(() => addTemplateDetail())
    } else {
      const res = formRef.current?.onFormRef()?.getFieldsValue()
      res.priority && res.startDate ? editTemplateDetail() : message.error('请检查必填项是否填写')
    }
  }

  useEffect(() => {
    if (open) {
      getQuoteType()
      // 编辑时设置表单值
      if (!isAdd) {
        formRef.current?.onFormRef()?.setFieldsValue({
          priority: clickInfo.priority,
          startDate: dayjs(clickInfo.startDate),
          endDate: clickInfo?.endDate ? dayjs(clickInfo.endDate) : undefined,
        })
      }
    } else {
      formRef.current?.onFormRef()?.resetFields()
    }
  }, [open, clickInfo, isAdd])

  useEffect(() => {
    if (budgetTypeValue) {
      getQuoteContract()
    }
  }, [budgetTypeValue, getQuoteContract])

  // 禁用开始日期之前的结束日期
  const disabledEndDate = current => {
    if (!current) return false
    const startDate = formRef.current?.onFormRef()?.getFieldValue('startDate')
    return startDate ? current.isBefore(startDate, 'day') : false
  }

  // 禁用结束日期之后的开始日期
  const disabledStartDate = current => {
    if (!current) return false
    const endDate = formRef.current?.onFormRef()?.getFieldValue('endDate')
    return endDate ? current && current.isAfter(endDate, 'day') : false
  }

  return (
    <SDrawerOrModal
      title={isAdd ? '新增模板配置' : '编辑'}
      open={open}
      onCancel={onCancel}
      width={500}
      antdProps={{
        footer: (
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: '10px' }} onClick={onCancel}>
              取消
            </Button>
            <Button type="primary" onClick={onSave} loading={submitLoading}>
              保存
            </Button>
          </div>
        ),
      }}
    >
      <div style={{ margin: '0 10px' }}>
        <SGeneralForms
          ref={formRef}
          layout={'vertical'}
          configs={[
            {
              formItem: [
                {
                  label: '报价类型',
                  name: 'quoteType',
                  spanCol: 24,
                  required: true,
                  type: 'select',
                  rules: [{ required: true, message: '报价类型不能为空' }],
                  comConfig: {
                    fieldNames: { label: 'name', value: 'code' },
                    options: budgetList,
                    style: { width: 460 },
                    onChange: (value: string) => {
                      setBudgetTypeValue(value)
                      // 清空合同配置选择
                      formRef.current?.onFormRef()?.setFieldValue('quotaContractId', undefined)
                    },
                  },
                  hidden: !isAdd,
                },
                {
                  label: '报价合同配置',
                  name: 'quotaContractId',
                  spanCol: 24,
                  required: true,
                  type: 'select',
                  rules: [{ required: true, message: '报价合同配置不能为空' }],
                  comConfig: {
                    options: quoteContractList,
                    style: { width: 460 },
                    loading: quoteContractLoading,
                    disabled: quoteContractLoading,
                  },
                  hidden: !isAdd || !budgetTypeValue,
                },
                {
                  label: '顺序',
                  name: 'priority',
                  spanCol: 24,
                  required: true,
                  type: 'inputNumber',
                  rules: [{ required: true, message: '顺序不能为空' }],
                  comConfig: {
                    style: { width: 460 },
                  },
                },
                {
                  label: '适用门店',
                  name: 'orgIdList',
                  spanCol: 24,
                  required: true,
                  type: 'select',
                  rules: [{ required: true, message: '适用门店不能为空' }],
                  comConfig: {
                    mode: 'multiple',
                    options: companyList,
                    fieldNames: { label: 'name', value: 'id' },
                    style: { width: 460 },
                    onChange: (value: string[]) => {
                      // 判断是否新增了"通用"选项
                      const hasAddedGeneral = value[value.length - 1] === '0'

                      if (hasAddedGeneral) {
                        // 如果新增了"通用"，则只保留"通用"
                        formRef.current?.onFormRef()?.setFieldValue('orgIdList', ['0'])
                      } else if (!hasAddedGeneral && value.includes('0')) {
                        // 如果新增了其他选项，且已选择了"通用"，则移除"通用"
                        const newValue = value.filter(v => v !== '0')
                        formRef.current?.onFormRef()?.setFieldValue('orgIdList', newValue)
                      }
                    },
                  },
                  hidden: !isAdd,
                },
                {
                  label: '开始日期',
                  name: 'startDate',
                  spanCol: 24,
                  required: true,
                  type: 'date',
                  rules: [{ required: true, message: '开始日期不能为空' }],
                  comConfig: {
                    style: { width: 460 },
                    onChange: date => {
                      const endDate = formRef.current?.onFormRef()?.getFieldValue('endDate')
                      if (endDate && date && date.isAfter(endDate)) {
                        formRef.current?.onFormRef()?.setFieldValue('endDate', null)
                      }
                    },
                    disabledDate: disabledStartDate,
                  },
                },
                {
                  label: '结束日期',
                  name: 'endDate',
                  spanCol: 24,
                  type: 'date',
                  comConfig: {
                    style: { width: 460 },
                    onChange: date => {
                      const startDate = formRef.current?.onFormRef()?.getFieldValue('startDate')
                      if (startDate && date && date.isBefore(startDate)) {
                        formRef.current?.onFormRef()?.setFieldValue('startDate', null)
                      }
                    },
                    disabledDate: disabledEndDate,
                  },
                },
              ],
            },
          ]}
        />
      </div>
    </SDrawerOrModal>
  )
}

export default AddOrEditDrawer
